import { FC, useEffect, useMemo, useState } from 'react'
import gql from 'graphql-tag'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { ArrowLeft, Close } from 'svgIcons'
import { useGqlNewsReactionModalQuery } from 'generated/graphql'
import useNewsReactionData from '@app.feature/channel/hook/useNewsReactionData'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import Modal, { IFModalProps } from 'app.components/Modal/Modal'
import { mobileBreakPoint } from 'app.styled/media'
import NewsReactionList from './NewsReactionList'

gql`
  fragment NewsReactionModal_User on User {
    id
    type
    username
    avatarUrl
    bookmarks {
      nodes {
        id
        content
        channel {
          id
          url
        }
      }
    }
    likes {
      nodes {
        id
        content
        channel {
          id
          url
        }
      }
    }
    organization {
      id
      name
      logoImage {
        id
        url
      }
    }
  }

  fragment NewsReactionModal_Channel on Channel {
    id
    url
    name
    briefDescription
    viewerIsOwner
    viewerHasFollowed
    owner {
      id
      logoImage {
        id
        url
      }
    }
  }

  fragment NewsReactionModal_NewsItem on NewsItem {
    id
    likers {
      totalCount
      nodes {
        ...NewsReactionModal_User
        ...NewsReactionModal_Channel
      }
    }
    bookmarkers {
      nodes {
        ...NewsReactionModal_User
        ...NewsReactionModal_Channel
      }
    }
  }

  query gqlNewsReactionModal($id: ID!) {
    newsItem(id: $id) {
      ...NewsReactionModal_NewsItem
    }
  }
`

type NewsReactionModalProps = IFModalProps

const NewsReactionModal: FC<NewsReactionModalProps> = ({
  onClose,
  ...props
}) => {
  const router = useRouter()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()
  const [currentTab, setCurrentTab] = useState('like')

  const { data, loading } = useGqlNewsReactionModalQuery({
    variables: {
      id: `${router.query.id}`,
    },
  })

  useEffect(() => {
    setCurrentTab(`${router.query.modal}`)
  }, [router.query])

  const tabList = useMemo(() => {
    return [
      {
        label: '좋아요',
        active: currentTab === 'like',
        onClick: () => setCurrentTab('like'),
      },
      {
        label: '스크랩',
        active: currentTab === 'scrap',
        onClick: () => setCurrentTab('scrap'),
      },
    ]
  }, [currentTab])

  const { dataSet } = useNewsReactionData(data?.newsItem, currentTab)

  return (
    <Modal onClose={onClose} {...props} animation={isMobile ? 'left' : 'fade'}>
      <StyledWrapper>
        {!isWebview && (
          <div className="header">
            <ArrowLeft className="mobile-close" onClick={onClose} />
            <div className="modal-title">소식 반응</div>
            <Close className="desktop-close" onClick={onClose} />
          </div>
        )}
        <div className="tab-box">
          {tabList.map((tab) => (
            <a
              key={tab.label}
              className="tab"
              data-active={tab.active}
              onClick={tab.onClick}
            >
              {tab.label}
            </a>
          ))}
        </div>
        <NewsReactionList data={dataSet} loading={loading} />
      </StyledWrapper>
    </Modal>
  )
}

export default NewsReactionModal

const StyledWrapper = styled.div`
  width: 550px;
  height: 640px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 20px;
  .header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 30px 30px 5px;
    .modal-title {
      font-size: 20px;
      font-weight: 500;
    }
    svg {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
    .mobile-close {
      display: none;
      position: absolute;
      top: 20px;
      left: 20px;
    }
  }
  .tab-box {
    display: flex;
    border-bottom: 1px solid #eee;
    .tab {
      width: 100%;
      height: 50px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 16px;
      font-weight: 500;
      &[data-active='true'] {
        color: #01a0ff;
        font-weight: bold;
        border-bottom: 2px solid #01a0ff;
      }
    }
  }

  ${mobileBreakPoint} {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    transform: none;
    border-radius: 0;
    .header {
      width: 100%;
      display: flex;
      justify-content: center;
      padding: 20px;
      .modal-title {
        font-size: 16px;
        font-weight: bold;
      }
      .mobile-close {
        display: block;
      }
      .desktop-close {
        display: none;
      }
    }
  }
`
